import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { useBookingAvailability, useBookingSlots, useCreateBooking, useResources } from "@/api/hooks/useBookings";
import type { BookingStatus } from "@/api/types/booking";
import { totalPriceCentsPreview } from "./bookingMath";

interface CreateBookingDialogProps {
  open: boolean;
  onClose: () => void;
  defaultResourceId?: string;
  defaultDate?: string;
  onCreated?: () => void;
}

/**
 * Staff booking-create form: live availability (disables an already-taken
 * slot in the dropdown, same call the calendar view uses) and a live price
 * preview including the extra-hour math (features/bookings/bookingMath.ts —
 * PREVIEW ONLY; the booking's real `price_cents`, returned by the create
 * call, is always the number of record).
 */
export function CreateBookingDialog({ open, onClose, defaultResourceId, defaultDate, onCreated }: CreateBookingDialogProps) {
  const { data: resources } = useResources();
  const [resourceId, setResourceId] = useState(defaultResourceId ?? "");
  const [bookingDate, setBookingDate] = useState(defaultDate ?? "");
  const [slotId, setSlotId] = useState("");
  const [extraHour, setExtraHour] = useState(false);
  const [status, setStatus] = useState<BookingStatus>("confirmed");
  const [headcount, setHeadcount] = useState("");
  const [occasion, setOccasion] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setResourceId(defaultResourceId ?? "");
      setBookingDate(defaultDate ?? "");
      setSlotId("");
      setExtraHour(false);
      setStatus("confirmed");
      setHeadcount("");
      setOccasion("");
      setNotes("");
    }
  }, [open, defaultResourceId, defaultDate]);

  const resource = useMemo(() => resources?.find((r) => r.id === resourceId), [resources, resourceId]);
  const isEventRoom = resource?.kind === "event_room";

  const { data: slots } = useBookingSlots(resource?.kind);
  const { data: availability } = useBookingAvailability(resourceId || undefined, bookingDate, bookingDate);
  const dayAvailability = availability?.days[0];

  const createBooking = useCreateBooking();

  const priceCents = resource && bookingDate ? totalPriceCentsPreview(resource, bookingDate, extraHour) : null;

  const selectedSlotTaken =
    isEventRoom && slotId
      ? dayAvailability?.slots.find((s) => s.slot_id === slotId)?.available === false
      : false;
  const dayTaken = !isEventRoom && dayAvailability ? dayAvailability.slots[0]?.available === false : false;

  const canSubmit =
    !!resourceId &&
    !!bookingDate &&
    (!isEventRoom || !!slotId) &&
    !selectedSlotTaken &&
    !dayTaken &&
    !createBooking.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await createBooking.mutateAsync({
        resource_id: resourceId,
        slot_id: isEventRoom ? slotId : null,
        booking_date: bookingDate,
        extra_hour: isEventRoom ? extraHour : false,
        status,
        headcount: headcount.trim() ? Number(headcount) : null,
        occasion: occasion.trim() || null,
        notes: notes.trim() || null,
      });
      toastSuccess("Booking created");
      onCreated?.();
      onClose();
    } catch (err) {
      toastError("Couldn't create booking", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="New booking">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>New booking</DialogHeader>
        <DialogBody className="space-y-4">
          <FormSelect
            label="Resource"
            value={resourceId}
            onChange={(e) => {
              setResourceId(e.target.value);
              setSlotId("");
            }}
          >
            <option value="">Select a resource…</option>
            {(resources ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </FormSelect>

          <FormField
            label="Date"
            type="date"
            value={bookingDate}
            onChange={(e) => {
              setBookingDate(e.target.value);
              setSlotId("");
            }}
          />

          {isEventRoom && (
            <FormSelect label="Time slot" value={slotId} onChange={(e) => setSlotId(e.target.value)}>
              <option value="">Select a slot…</option>
              {(slots ?? []).map((s) => {
                const slotAvail = dayAvailability?.slots.find((a) => a.slot_id === s.id);
                const taken = bookingDate && slotAvail && !slotAvail.available;
                return (
                  <option key={s.id} value={s.id} disabled={!!taken}>
                    {s.label}
                    {taken ? " — already booked" : ""}
                  </option>
                );
              })}
            </FormSelect>
          )}

          {!isEventRoom && resourceId && bookingDate && dayTaken && (
            <p className="text-sm text-danger" role="alert">
              This resource is already booked on this date.
            </p>
          )}

          {isEventRoom && (
            <Checkbox
              label="Add an adjacent hour (+25% of the block price)"
              checked={extraHour}
              onChange={(e) => setExtraHour(e.target.checked)}
            />
          )}

          <FormSelect label="Status" value={status} onChange={(e) => setStatus(e.target.value as BookingStatus)}>
            <option value="confirmed">Confirmed</option>
            <option value="inquiry">Inquiry</option>
          </FormSelect>

          <FormField
            label="Headcount (optional)"
            type="number"
            min={0}
            value={headcount}
            onChange={(e) => setHeadcount(e.target.value)}
          />

          <FormField
            label="Occasion (optional)"
            type="text"
            placeholder="e.g. Baby shower"
            value={occasion}
            onChange={(e) => setOccasion(e.target.value)}
          />

          <FormTextarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />

          {priceCents !== null && (
            <p className="text-sm text-text-secondary" data-testid="booking-price-preview">
              Price preview: <span className="font-semibold text-text-primary">{centsToUsd(priceCents)}</span>
              {resource?.is_whole_house && " (flat rate)"}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createBooking.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {createBooking.isPending ? "Creating…" : "Create booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
